import { STREAM_KEYS } from 'lib/liveVideo';
import getConfig from 'next/config';

const {
  publicRuntimeConfig: {
    CODE_ENVIRONMENT,
  },
} = getConfig();

const isProduction = CODE_ENVIRONMENT === 'production';

/**
 * Conditional stream key selection
 * @param {string} prodKey
 * @param {string} stagingKey
 * @param {boolean} showLocalStagingStreams
 */
function getStreamKey(prodKey, stagingKey, showLocalStagingStreams) {
  return (showLocalStagingStreams && !isProduction ? stagingKey : prodKey);
}

/**
 * @type {Function}
 * @param {boolean} showLocalStagingStreams Is the local straging stream feature flag on
 * @returns {object}
*/
// the order of this map directly affects the order of the channels rendered by <FastChannelSelector />
export const fastChannelWatchStreamNameMap = (showLocalStagingStreams = false) => (
  {
    news: {
      streamKey: STREAM_KEYS.NEWS_NOW,
      hashUrl: ' ',
      scheduleKey: 'NEWS_NOW',
    },
    nyc: {
      streamKey: getStreamKey(STREAM_KEYS.NYC, STREAM_KEYS.NYC_STAGING, showLocalStagingStreams),
      hashUrl: '#new-york',
      scheduleKey: 'NY',
    },
    la: {
      streamKey: getStreamKey(STREAM_KEYS.LA, STREAM_KEYS.LA_STAGING, showLocalStagingStreams),
      hashUrl: '#los-angeles',
      scheduleKey: 'LA',
    },
    chicago: {
      streamKey: getStreamKey(STREAM_KEYS.CHICAGO, STREAM_KEYS.CHICAGO_STAGING, showLocalStagingStreams),
      hashUrl: '#chicago',
      scheduleKey: 'CHICAGO',
    },
    dallasFortWorth: {
      streamKey: getStreamKey(STREAM_KEYS.DFW, STREAM_KEYS.DFW_STAGING, showLocalStagingStreams),
      hashUrl: '#dallas-fort-worth',
      scheduleKey: 'DFW',
    },
    philly: {
      streamKey: getStreamKey(STREAM_KEYS.PHILADELPHIA, STREAM_KEYS.PHILADELPHIA_STAGING, showLocalStagingStreams),
      hashUrl: '#philadelphia',
      scheduleKey: 'PHILADELPHIA',
    },
    dc: {
      streamKey: getStreamKey(STREAM_KEYS.DC, STREAM_KEYS.DC_STAGING, showLocalStagingStreams),
      hashUrl: '#washington',
      scheduleKey: 'DC',
    },
    boston: {
      streamKey: getStreamKey(STREAM_KEYS.BOSTON, STREAM_KEYS.BOSTON_STAGING, showLocalStagingStreams),
      hashUrl: '#boston',
      scheduleKey: 'BOSTON',
    },
    bayarea: {
      streamKey: getStreamKey(STREAM_KEYS.BAY_AREA, STREAM_KEYS.BAY_AREA_STAGING, showLocalStagingStreams),
      hashUrl: '#bay-area',
      scheduleKey: 'SF_BAY',
    },
    southFlorida: {
      streamKey: getStreamKey(STREAM_KEYS.MIAMI, STREAM_KEYS.MIAMI_STAGING, showLocalStagingStreams),
      hashUrl: '#miami',
      scheduleKey: 'SOUTH_FLORIDA',
    },
    sd: {
      streamKey: getStreamKey(STREAM_KEYS.SD, STREAM_KEYS.SD_STAGING, showLocalStagingStreams),
      hashUrl: '#san-diego',
      scheduleKey: 'SAN_DIEGO',
    },
    connecticut: {
      streamKey: getStreamKey(STREAM_KEYS.CONNECTICUT, STREAM_KEYS.CONNECTICUT_STAGING, showLocalStagingStreams),
      hashUrl: '#connecticut',
      scheduleKey: 'CONNECTICUT',
    },
    sports: {
      streamKey: STREAM_KEYS.SPORTS,
      hashUrl: '#sports-now',
      scheduleKey: 'NBC_SPORTS_NOW',
    },
    today: {
      streamKey: STREAM_KEYS.TODAY_ALL_DAY,
      hashUrl: '#allday',
      scheduleKey: 'TODAY_ALL_DAY',
    },
    dateline: {
      streamKey: STREAM_KEYS.DATELINE,
      hashUrl: '#dateline',
      scheduleKey: 'DL247',
    },
    sky: {
      streamKey: STREAM_KEYS.SKY_NEWS,
      hashUrl: '#skynews',
      scheduleKey: 'SKYNEWS',
    },
    noticias: {
      streamKey: STREAM_KEYS.NOTICIAS_AHORA,
      hashUrl: '#noticias-telemundo-ahora',
      scheduleKey: 'NOTICIAS_AHORA',
    },
    telemundo_al_dia: {
      streamKey: STREAM_KEYS.TELEMUNDO_AL_DIA,
      hashUrl: '#telemundo-al-dia',
      scheduleKey: 'TELEMUNDO_AL_DIA',
    },
    telemundo_deportes_ahora: {
      streamKey: STREAM_KEYS.TELEMUNDO_DEPORTES_AHORA,
      hashUrl: '#telemundo-deportes-ahora',
      scheduleKey: 'TELEMUNDO_DEPORTES_AHORA',
    },
    telemundo_florida: {
      streamKey: getStreamKey(STREAM_KEYS.TELEMUNDO_FLORIDA, STREAM_KEYS.TELEMUNDO_FLORIDA_STAGING, showLocalStagingStreams),
      hashUrl: '#telemundo-florida',
      scheduleKey: 'TELEMUNDO_FLORIDA',
    },
    telemundo_noreste: {
      streamKey: getStreamKey(STREAM_KEYS.TELEMUNDO_NORESTE, STREAM_KEYS.TELEMUNDO_NORESTE_STAGING, showLocalStagingStreams),
      hashUrl: '#telemundo-noreste',
      scheduleKey: 'TELEMUNDO_NORESTE',
    },
    telemundo_texas: {
      streamKey: getStreamKey(STREAM_KEYS.TELEMUNDO_TEXAS, STREAM_KEYS.TELEMUNDO_TEXAS_STAGING, showLocalStagingStreams),
      hashUrl: '#telemundo-texas',
      scheduleKey: 'TELEMUNDO_TEXAS',
    },
    telemundo_california: {
      streamKey: getStreamKey(STREAM_KEYS.TELEMUNDO_CALIFORNIA, STREAM_KEYS.TELEMUNDO_CALIFORNIA_STAGING, showLocalStagingStreams),
      hashUrl: '#telemundo-california',
      scheduleKey: 'TELEMUNDO_CALIFORNIA',
    },
  }
);

/**
 * Telemundo Fast Channel Stream Name Map
 *
 * @type {Function}
 * @returns {object}
 *
 * the order of this map directly affects the order of the channels rendered by <FastChannelSelector />
*/
export const fastChannelAhoraStreamNameMap = (showLocalStagingStreams) => {
  const watchMap = fastChannelWatchStreamNameMap(showLocalStagingStreams);
  return {
    noticias: watchMap.noticias,
    telemundo_al_dia: watchMap.telemundo_al_dia,
    telemundo_deportes_ahora: watchMap.telemundo_deportes_ahora,
  };
};

/**
 * Get the fast channel stream name map based on the vertical and staging options.
 * @param {string} vertical - The vertical to get the stream name map for.
 * @param {boolean} showLocalStagingStreams - Whether to show local staging streams.
 * @returns {object} - The fast channel stream name map.
 */
export const getFastChannelStreamNameMap = (vertical, showLocalStagingStreams = false) => {
  if (vertical === 'telemundo') {
    return fastChannelAhoraStreamNameMap(showLocalStagingStreams);
  }
  return fastChannelWatchStreamNameMap(showLocalStagingStreams);
};
